import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, Eye, RefreshCw, Film, Camera } from 'lucide-react';
import { api } from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ViewAnalyticsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('total_views');
  const [sortDir, setSortDir] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  const [generatedAt, setGeneratedAt] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics/views`, {
        headers: { Authorization: `Bearer ${api.getToken()}` },
      });
      const data = await res.json();
      setRows(data.rows || []);
      setGeneratedAt(data.generated_at || '');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = useMemo(() => {
    let r = filterType === 'all' ? rows : rows.filter(x => x.item_type === filterType);
    r = [...r].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return r;
  }, [rows, filterType, sortKey, sortDir]);

  const totals = useMemo(() => {
    const t = { collection_total: 0, film_total: 0, collection_30d: 0, film_30d: 0 };
    rows.forEach(r => {
      if (r.item_type === 'collection') { t.collection_total += r.total_views; t.collection_30d += r.views_30d; }
      if (r.item_type === 'film')       { t.film_total       += r.total_views; t.film_30d       += r.views_30d; }
    });
    return t;
  }, [rows]);

  const arrow = (k) => sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <div data-testid="view-analytics-panel">
      <div className="flex flex-wrap items-end justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-bold">View Analytics</h2>
          <p className="text-sm text-gray-500">Page views per Collection and Film. Server de-dupes by IP within a 30-minute window.</p>
        </div>
        <button type="button" onClick={load} className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 hover:bg-gray-50">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard icon={<Camera size={16} />} label="Collection views (all-time)" value={totals.collection_total} />
        <SummaryCard icon={<Camera size={16} />} label="Collection views (30d)" value={totals.collection_30d} accent />
        <SummaryCard icon={<Film size={16} />} label="Film views (all-time)" value={totals.film_total} />
        <SummaryCard icon={<Film size={16} />} label="Film views (30d)" value={totals.film_30d} accent />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-3">
        {[['all', 'All'], ['collection', 'Collections'], ['film', 'Films']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilterType(k)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${filterType === k ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200'}`}
            data-testid={`view-analytics-filter-${k}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="animate-spin inline" /></div>
      ) : (
        <div className="overflow-x-auto border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 cursor-pointer" onClick={() => toggleSort('item_type')}>Type{arrow('item_type')}</th>
                <th className="px-3 py-2 cursor-pointer" onClick={() => toggleSort('label')}>Item{arrow('label')}</th>
                <th className="px-3 py-2 text-right cursor-pointer" onClick={() => toggleSort('total_views')}>Total{arrow('total_views')}</th>
                <th className="px-3 py-2 text-right cursor-pointer" onClick={() => toggleSort('views_30d')}>30 days{arrow('views_30d')}</th>
                <th className="px-3 py-2 cursor-pointer" onClick={() => toggleSort('last_viewed_at')}>Last viewed{arrow('last_viewed_at')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr><td colSpan={5} className="py-8 text-center text-gray-400">No data yet — views will start appearing as visitors browse.</td></tr>
              ) : sorted.map(r => (
                <tr key={`${r.item_type}-${r.item_id}`} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider ${r.item_type === 'film' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {r.item_type === 'film' ? <Film size={10} /> : <Camera size={10} />}
                      {r.item_type}
                    </span>
                  </td>
                  <td className="px-3 py-2">{r.label || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.total_views}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.views_30d}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{r.last_viewed_at ? new Date(r.last_viewed_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {generatedAt && (
        <p className="text-xs text-gray-400 mt-3">Generated {new Date(generatedAt).toLocaleString()}</p>
      )}
    </div>
  );
};

const SummaryCard = ({ icon, label, value, accent }) => (
  <div className={`border p-3 ${accent ? 'border-black/30 bg-black/[0.02]' : 'border-gray-200 bg-white'}`}>
    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">{icon} <span>{label}</span></div>
    <p className="text-2xl font-bold tabular-nums">{value.toLocaleString()}</p>
  </div>
);

export default ViewAnalyticsPanel;
